import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type Point = readonly [number, number];
export type Stroke = Point[];

export const PAD_W = 480;
export const PAD_H = 160;

/**
 * Strokes → a small standalone SVG (what the server stores verbatim and hashes). Coordinates are rounded to 1 decimal,
 * a lone tap becomes a dot. Returns null when nothing meaningful was drawn.
 */
export function strokesToSvg(strokes: Stroke[], width = PAD_W, height = PAD_H): string | null {
  const parts: string[] = [];
  for (const s of strokes) {
    if (s.length === 0) continue;
    const f = (n: number) => Math.round(n * 10) / 10;
    const [x0, y0] = s[0]!;
    parts.push(s.length === 1 ? `M${f(x0)} ${f(y0)}l0.1 0.1` : `M${f(x0)} ${f(y0)}${s.slice(1).map(([x, y]) => `L${f(x)} ${f(y)}`).join("")}`);
  }
  if (parts.length === 0) return null;
  const points = strokes.reduce((n, s) => n + s.length, 0);
  if (points < 8) return null; // a scribble of a few points is not a signature
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><path d="${parts.join("")}" fill="none" stroke="#111" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

interface Props {
  /** Called with the SVG whenever the drawing changes (null when empty). */
  onChange: (svg: string | null) => void;
}

/** Touch / mouse / stylus signature pad (pointer events). The drawing is also kept as plain strokes for the SVG export. */
export function SignaturePad({ onChange }: Props) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Stroke[]>([]);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const redraw = () => {
    const ctx = canvas.current?.getContext?.("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, PAD_W, PAD_H);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes.current) {
      ctx.beginPath();
      s.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      if (s.length === 1) ctx.lineTo(s[0]![0] + 0.1, s[0]![1] + 0.1);
      ctx.stroke();
    }
  };

  const publish = () => {
    const svg = strokesToSvg(strokes.current);
    setEmpty(!svg);
    onChange(svg);
  };

  const pos = (e: React.PointerEvent): Point => {
    const r = canvas.current!.getBoundingClientRect();
    return [((e.clientX - r.left) / (r.width || PAD_W)) * PAD_W, ((e.clientY - r.top) / (r.height || PAD_H)) * PAD_H];
  };

  useEffect(redraw, []);

  return (
    <div>
      <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-white">
        <canvas
          ref={canvas} width={PAD_W} height={PAD_H} aria-label={t("waiver.sign")} data-testid="signature-pad"
          className="block h-40 w-full touch-none"
          onPointerDown={(e) => { e.preventDefault(); canvas.current?.setPointerCapture?.(e.pointerId); drawing.current = true; strokes.current.push([pos(e)]); redraw(); }}
          onPointerMove={(e) => { if (!drawing.current) return; strokes.current.at(-1)!.push(pos(e)); redraw(); }}
          onPointerUp={() => { drawing.current = false; publish(); }}
          onPointerCancel={() => { drawing.current = false; publish(); }}
        />
        {empty && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-300">{t("waiver.sign")}</span>}
      </div>
      <button type="button" className="btn-ghost mt-2 !px-3 !py-1 text-xs" onClick={() => { strokes.current = []; redraw(); publish(); }}>{t("waiver.clear")}</button>
    </div>
  );
}
